import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { MuiButton } from '@ace/ace-ui-web';
import lang from '../../languages/en.json';
import styles from './PrintPageWrapper.module.css';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface StoreHeaderInfo {
  storeLine: string;
  isMultiStore: boolean;
}

export interface SkeletonConfig {
  /** Number of card-sized skeletons in the middle row */
  cardCount: number;
  /** Height of each card skeleton (px) */
  cardHeight: number;
  /** Number of grid/section skeletons below the cards */
  sectionCount: number;
}

interface PrintPageWrapperProps {
  /** Page title shown in the toolbar, error state, page h1, and browser tab */
  title: string;
  /** ID applied to the report container div (used for PDF polling) */
  containerId: string;
  /** Whether the report data is ready to render */
  isContentReady: boolean;
  /** Whether the parent tab is unavailable — shows error state when true */
  isParentUnavailable: boolean;
  /** Called when the Print button is clicked */
  onPrint: () => void;
  /** Store info for the repeating per-page header */
  storeHeaderInfo: StoreHeaderInfo;
  /** Formatted report generation date string (e.g. "April 8, 2026") */
  reportDate: string;
  /** Controls the loading skeleton placeholders */
  skeletonConfig: SkeletonConfig;
  /** Report-specific body content rendered inside the page shell */
  children: React.ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * PrintPageWrapper
 *
 * Shared shell used by both OperationsPrintView and PerformancePrintView.
 * Renders:
 *  - Error state when parent tab is unavailable
 *  - Sticky toolbar with Print button
 *  - Loading skeleton while data is being read
 *  - <table><thead> repeating store/date header on every printed page
 *  - Page title h1
 *  - Slot for the report-specific body content via `children`
 */
const PrintPageWrapper: React.FC<PrintPageWrapperProps> = ({
  title,
  containerId,
  isContentReady,
  isParentUnavailable,
  onPrint,
  storeHeaderInfo,
  reportDate,
  skeletonConfig,
  children,
}): JSX.Element => {
  // ─── Error state ─────────────────────────────────────────────────────────

  if (isParentUnavailable) {
    return (
      <Box className={styles.pageWrapper}>
        <Box className={styles.printToolbar}>
          <Box className={styles.printToolbarInner}>
            <Typography
              variant="h6"
              sx={{ flexGrow: 1, fontWeight: 'bold' }}>
              {title}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ maxWidth: 600, margin: '4rem auto', textAlign: 'center', padding: '0 1rem' }}>
          <Typography
            variant="h6"
            color="error"
            sx={{ mb: 2 }}>
            {lang.pageContent.parentTabClosed}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box className={styles.pageWrapper}>
      {/* Sticky toolbar — hidden via @media print */}
      <Box className={styles.printToolbar}>
        <Box className={styles.printToolbarInner}>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            {title}
          </Typography>
          <MuiButton
            colors="tertiary"
            variant="outlined"
            iconPosition="left"
            iconType="localPrintshopOutlined"
            onClick={onPrint}
            disabled={!isContentReady}>
            {lang.pageContent.printPage}
          </MuiButton>
        </Box>
      </Box>

      {/* Loading skeleton */}
      {!isContentReady && (
        <Box
          className={styles.printContainer}
          sx={{ padding: '1rem' }}>
          <Skeleton
            variant="rectangular"
            height={60}
            sx={{ mb: 2 }}
          />
          <Skeleton
            variant="rectangular"
            height={40}
            sx={{ mb: 2, mx: 'auto', width: '50%' }}
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {Array.from({ length: skeletonConfig.cardCount }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={skeletonConfig.cardHeight}
                sx={{ flex: 1 }}
              />
            ))}
          </Box>
          {Array.from({ length: skeletonConfig.sectionCount }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={200}
              sx={{ mb: 2 }}
            />
          ))}
        </Box>
      )}

      {/* Report content */}
      {isContentReady && (
        <div
          id={containerId}
          data-pdf-container
          className={styles.printContainer}>
          {/*
           * <table><thead> trick: browser natively repeats <thead> at the top
           * of every printed page without overlapping body content.
           */}
          <table className={styles.printTable}>
            <thead className={styles.printTableHead}>
              <tr>
                <td>
                  <header
                    className={styles.printHeader}
                    data-pdf-header>
                    <p className={styles.headerStoreLine}>
                      {storeHeaderInfo.storeLine}
                      {storeHeaderInfo.isMultiStore && (
                        <span className={styles.headerMultiStoreTag}>
                          {`, ${lang.pageContent.multiStoreMode}`}
                        </span>
                      )}
                    </p>
                    <p className={styles.headerReportDate}>
                      {`${lang.pageContent.reportGenerated}: ${reportDate}`}
                    </p>
                  </header>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={styles.printTableCell}>
                  {/* Page title */}
                  <h1
                    className={styles.pageTitle}
                    data-pdf-title>
                    {title}
                  </h1>

                  {/* Report-specific content */}
                  {children}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Box>
  );
};

export default PrintPageWrapper;
